<script setup lang="ts">
    import $ from "jquery"
    import { computed, onMounted, onBeforeUnmount, watch, nextTick, ref, type Ref } from "vue"
    import { useRoute } from "vue-router"

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue'

    import { MonthYearHelpers } from '@common/types'
    import { useSchedulingPageQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'
    import type { SchedulingPageReadModel } from '@Quest/PortfolioScenarios/readmodels'

    const kendo$ = ((window as any).kendo?.jQuery ?? $) as JQueryStatic

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params?.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params?.scenarioId ?? ''))

    const {
        runSchedulingPageQueryV1,
        modelSchedulingPageQueryV1
    } = useSchedulingPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelSchedulingPageQueryV1 as Ref<SchedulingPageReadModel | null>

    const ds = computed(() =>
    {
        if (!rm.value) return []

        return rm.value.Projects
            .filter(a => a.EndDate)
            .map(a =>
            {
                const start = MonthYearHelpers.toDate(
                    MonthYearHelpers.buildFromMonthYearString(a.StartDate)
                )

                const end = MonthYearHelpers.toDate(
                    MonthYearHelpers.buildFromMonthYearString(a.EndDate)
                )

                return {
                    id: a.Id,
                    title: a.Name,
                    start,
                    end
                }
            })
    })

    const ganttRef = ref<HTMLElement | null>(null)

    function edit(e: any)
    {
        const selection = e.sender.select()
        const task = e.sender.dataItem(selection)

        const alternative = rm.value?.Projects.find(a => a.Id === task.id)
    }

    function ensureGantt()
    {
        const el = ganttRef.value
        if (!el) return

        const existing = (kendo$(el) as any).data("kendoGantt")

        if (existing)
        {
            existing.setDataSource(
                new (window as any).kendo.data.GanttDataSource({
                    data: ds.value
                })
            )
            return
        }

        (kendo$(el) as any).kendoGantt({
            editable: false,
            height: "100%",
            tooltip: { visible: false },
            views: [
                { type: "year", selected: true }
            ],
            columns: [
                { field: "title", title: "Name", sortable: true },
                { field: "start", title: "Start", format: "{0:MM/yyyy}", sortable: true, width: 120 },
                { field: "end", title: "End", format: "{0:MM/yyyy}", sortable: true, width: 120 }
            ],
            dataSource: ds.value,
            change: (e: any) => edit(e)
        })
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Plan - Schedule"
        runSchedulingPageQueryV1()
    })

    watch(rm, async (current) =>
    {
        if (!current) return

        await nextTick()
        ensureGantt()
    })

    onBeforeUnmount(() =>
    {
        const gantt = (kendo$(ganttRef.value) as any)?.data("kendoGantt")
        if (gantt) gantt.destroy()
    })
</script>

<template>
    <div>
        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Schedule</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId" 
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl" />
                </div>
            </div>

            <hr />

            <div class="mt-5" style="height: calc(100vh - 300px);">
                <div ref="ganttRef" style="height:100%"></div>
            </div>

        </div>
    </div>

</template>

<style>
    .k-gantt-toolbar {
        display: none;
    }

    .k-task-template {
        cursor: pointer !important;
    }

    .k-gantt-treelist tr {
        cursor: pointer !important;
    }
</style>
